import { TokenService } from '../auth/token.service';
import { apiConfig } from '../../config/app.config';
// PRD-01: same required REACT_APP_API_BASE_URL as apiClient (was REACT_APP_API_URL, unset everywhere, with a dev fallback)
const API_BASE_URL = apiConfig.baseUrl;

export interface PatientImageUploadResponse {
  message: string;
  data: {
    success: boolean;
    patientId: string;
    profilePictureUrl: string;
  };
}

export interface PatientImageGetResponse {
  message: string;
  data: {
    success: boolean;
    patientId: string;
    hasProfilePicture: boolean;
    profilePictureUrl?: string;
    expiresAt?: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface PatientImageData {
  patientId: string;
  imageUrl: string | null;
  hasProfilePicture: boolean;
  expiresAt?: string;
}

/**
 * Get the in-memory access token
 */
const getAuthToken = (): string => {
  const token = TokenService.getAccessToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }
  return token;
};

/**
 * Convert a File or Blob to base64 string
 */
const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

class PatientImageService {
  private imageCache = new Map<string, PatientImageData>();
  private pendingRequests = new Map<string, Promise<PatientImageData>>();

  clearPatientImage(patientId: string): void {
    this.imageCache.delete(patientId);
  }

  /**
   * Upload a profile image for a patient
   */
  async uploadPatientImage(
    patientId: string,
    imageFile: File | Blob,
    contentType: string = 'image/jpeg'
  ): Promise<PatientImageUploadResponse> {
    try {
      if (!patientId) {
        throw new Error('Patient ID is required for image upload');
      }

      const token = getAuthToken();
      const base64Image = await fileToBase64(imageFile);

      const response = await fetch(`${API_BASE_URL}/patients/${patientId}/uploadpicture`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: base64Image,
          contentType: contentType,
        }),
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      this.imageCache.delete(patientId);

      return response.json();
    } catch (error) {
      console.error('Error uploading patient image:', error);
      throw new Error('Failed to upload patient image');
    }
  }

  /**
   * Get the profile image URL for a patient
   */
  async getPatientImage(patientId: string, expiresIn: number = 3600): Promise<PatientImageData> {
    const cachedImage = this.imageCache.get(patientId);
    if (cachedImage) {
      const expiresAt = cachedImage.expiresAt ? new Date(cachedImage.expiresAt).getTime() : 0;
      if (!expiresAt || expiresAt > Date.now() + 5 * 60 * 1000) {
        return Promise.resolve(cachedImage);
      }
      this.imageCache.delete(patientId);
    }

    const pendingRequest = this.pendingRequests.get(patientId);
    if (pendingRequest) {
      return pendingRequest;
    }

    const request = this.fetchPatientImage(patientId, expiresIn);
    this.pendingRequests.set(patientId, request);

    try {
      const image = await request;
      this.imageCache.set(patientId, image);
      return image;
    } finally {
      this.pendingRequests.delete(patientId);
    }
  }

  private async fetchPatientImage(patientId: string, expiresIn: number): Promise<PatientImageData> {
    try {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }

      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/patients/${encodeURIComponent(patientId)}/picture?expiresIn=${expiresIn}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.statusText}`);
      }

      const result: PatientImageGetResponse = await response.json();

      return {
        patientId: result.data.patientId,
        imageUrl: result.data.hasProfilePicture ? result.data.profilePictureUrl || null : null,
        hasProfilePicture: result.data.hasProfilePicture,
        expiresAt: result.data.expiresAt || new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      };
    } catch (error) {
      console.error('Error fetching patient image:', error);
      return {
        patientId,
        imageUrl: null,
        hasProfilePicture: false,
      };
    }
  }
}

export const patientImageService = new PatientImageService();
